/**
 * Google Calendar URL Generator
 *
 * Opens Google Calendar with pre-filled event data.
 * This approach keeps Google Calendar as the source of truth.
 *
 * Reference: https://github.com/niccokunzmann/add-to-calendar-button/wiki/Google-Calendar-URL-format
 */

/**
 * Generate a Google Calendar URL with pre-filled event data
 * @param {Object} params - Event parameters
 * @param {string} params.title - Event title
 * @param {Date} params.startDate - Start date/time
 * @param {Date} params.endDate - End date/time
 * @param {string} [params.description] - Event description
 * @param {string} [params.location] - Event location
 * @param {string[]} [params.attendees] - List of attendee emails
 * @returns {string} Google Calendar URL
 */
export function generateGoogleCalendarUrl({
  title,
  startDate,
  endDate,
  description = '',
  location = '',
  attendees = []
}) {
  const baseUrl = 'https://calendar.google.com/calendar/render';

  // Format dates as YYYYMMDDTHHMMSS (local time)
  const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    return `${year}${month}${day}T${hours}${minutes}${seconds}`;
  };

  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: title,
    dates: `${formatDate(startDate)}/${formatDate(endDate)}`,
  });

  if (description) {
    params.append('details', description);
  }

  if (location) {
    params.append('location', location);
  }

  // Add attendees (comma-separated emails)
  if (attendees.length > 0) {
    params.append('add', attendees.join(','));
  }

  return `${baseUrl}?${params.toString()}`;
}

/**
 * Open Google Calendar in a new tab with pre-filled event data
 * @param {Object} params - Same as generateGoogleCalendarUrl
 */
export function openGoogleCalendar(params) {
  const url = generateGoogleCalendarUrl(params);
  window.open(url, '_blank', 'noopener,noreferrer');
}

/**
 * Create a meeting URL for a deal
 * @param {Object} deal - Deal object
 * @param {Object} options - Meeting options
 * @param {Date} options.startDate - Meeting start date/time
 * @param {number} [options.durationMinutes=30] - Meeting duration in minutes
 * @param {string} [options.description] - Additional description
 * @param {string[]} [options.attendeeEmails] - Attendee emails
 * @returns {string} Google Calendar URL
 */
export function createMeetingUrlForDeal(deal, options) {
  const {
    startDate,
    durationMinutes = 30,
    description = '',
    attendeeEmails = [],
  } = options;

  // Calculate end time
  const endDate = new Date(startDate.getTime() + durationMinutes * 60 * 1000);

  // Build attendee list from deal contacts
  const attendees = [...attendeeEmails];
  if (deal._displayCache?.primaryContactEmail) {
    attendees.push(deal._displayCache.primaryContactEmail);
  }

  return generateGoogleCalendarUrl({
    title: `Meeting: ${deal.title}`,
    startDate,
    endDate,
    description: description || `Meeting regarding ${deal.title}`,
    attendees: [...new Set(attendees)], // Remove duplicates
  });
}

/**
 * Open Google Calendar for a deal meeting
 * @param {Object} deal - Deal object
 * @param {Object} options - Same as createMeetingUrlForDeal
 */
export function openMeetingForDeal(deal, options) {
  const url = createMeetingUrlForDeal(deal, options);
  window.open(url, '_blank', 'noopener,noreferrer');
}
